import { useContext, useEffect, useState } from 'react';
import { Box, Button, SvgIcon, Alert, Snackbar, Stack, useMediaQuery, useTheme } from '@mui/material';
import { Plus } from '../../assets';
import { useTranslation } from 'react-i18next';
import useLogError from '../../hooks/useLogError';
import { RecipeDialogProvider, useRecipeDialog } from '../../pages/Recipe/context/RecipeDialogContext';
import ItemsTab from './components/AllItems/ItemsTab';
import CreateRecipeDialog from '../../pages/Recipe/components/CreateRecipeDialog';
import { CreateMadeInStoreItemRequestDto } from '../../app/services/api/generated';
import useNswagClient from '../../hooks/api/useNswagClient';
import PageTitle from '../../components/shared/PageTitle';
import PageArea from '../../components/shared/PageArea';
import { UserContext } from '../../components/shared/useUser';

const ItemTabs = () => {
  const { t } = useTranslation('products');
  const theme = useTheme();

  const { hasPermissionTo } = useContext(UserContext);
  const [hasPermissionToCreateRecipe, setHasPermissionToCreateRecipe] = useState<boolean>(false);
  const { createMadeInStore } = useNswagClient();
  const { openRecipeDialog } = useRecipeDialog();
  const { logError } = useLogError();

  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [showSubmitStatusMessage, setShowSubmitStatusMessage] = useState<boolean>(false);
  const [isSubmittedSuccessfully, setIsSubmittedSuccessfully] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [itemCount, setItemCount] = useState<number | undefined>();

  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);

  useEffect(() => {
    setHasPermissionToCreateRecipe(hasPermissionTo(['CreateItemsAndRecipes']));
  }, []);


  const onCreateNew = () => {
    openRecipeDialog({ recipe: null, entityType: 0 });
  };

  const onSubmit = (item: CreateMadeInStoreItemRequestDto) => {
    setIsUpdating(true);
    createMadeInStore(item)
      .then(() => {
        setIsSubmittedSuccessfully((prev) => prev + 1);
      })
      .catch((error) => {
        logError(error);
        setErrorMessage(error);
        setIsSubmittedSuccessfully((prev) => prev + 1);
      })
      .finally(() => {
        setIsUpdating(false);
        setShowSubmitStatusMessage(true);
      });
  };

  return (
    <PageArea>
      <Stack width='100%'
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{
          justifyContent: 'space-between',
        }}>
        <Box width={isMobile ? '100%' : 'auto'}>
          <PageTitle subTitle={t('subTitle')}>{t('title')}</PageTitle>
        </Box>

        {hasPermissionToCreateRecipe && (
          <Box width={isMobile ? '100%' : 'auto'}
            sx={{
              display: 'flex',
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
            }}>
            <Button variant="primary"
              size="lg"
              startIcon={<SvgIcon> <Plus fill='white' /> </SvgIcon>}
              onClick={onCreateNew}
              sx={{
                mb: { xs: 4, md: 0 },
              }}
            >
              {t('createNew')}
            </Button>
          </Box>
        )}
      </Stack>
      <ItemsTab hasNewItemCreated={isSubmittedSuccessfully}
        itemCount={itemCount}
        setItemCount={setItemCount}
        onCreateNew={onCreateNew} />
      <CreateRecipeDialog
        isUpdating={isUpdating}
        onCreateConfirm={onSubmit}
        onUpdateConfirm={() => { }}
        isItemsPage={true}
        isComplete={isSubmittedSuccessfully}
      />
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showSubmitStatusMessage}
        onClose={() => setShowSubmitStatusMessage(false)}
        autoHideDuration={5000}
      >
        <Alert
          onClose={() => setShowSubmitStatusMessage(false)}
          severity={isSubmittedSuccessfully ? 'success' : 'error'}
          variant='filled'
          sx={{ width: '100%' }}
        >
          {isSubmittedSuccessfully ? t('successMessage') : t('errorMessage') + ' ' + errorMessage}
        </Alert>
      </Snackbar>
    </PageArea>
  );
};

const ProductsPage = () => {
  return (
    <RecipeDialogProvider>
      <ItemTabs />
    </RecipeDialogProvider>
  );
};

export default ProductsPage;